import React, { useState } from 'react';
import CMSCustomSections from '../components/CMSCustomSections';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  Clock, 
  CheckCircle, 
  Car, 
  User, 
  Calendar,
  Shield,
  Zap,
  Target,
  ArrowRight
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { createConsultation } from '../api';
import { useCMS } from '../hooks/useCMS';

const Consultation = () => {
  const navigate = useNavigate();
  const { content } = useCMS('consultation');
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consultationType: '',
    vehicleInterest: '',
    currentSituation: '',
    timeframe: '',
    budget: '',
    preferredContact: 'phone',
    preferredTime: '',
    additionalNotes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedLead, setSubmittedLead] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await createConsultation(formData);
      setSubmittedLead(response);
      toast({
        title: "Consultation Request Received!",
        description: `Reference: ${response.leadId}. We'll contact you within 24 hours.`
      });
      setStep(4); // Show success state
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const consultationTypes = [
    {
      value: 'driven',
      icon: User,
      title: 'Being DRIVEN',
      description: 'Chauffeur services, wedding transport, corporate events'
    },
    {
      value: 'driving',
      icon: Car,
      title: 'DRIVING Services',
      description: 'Vehicle acquisition, AuctionWell, fleet management'
    },
    {
      value: 'both',
      icon: Shield,
      title: 'Full Auto Lifestyle',
      description: 'Complete mobility concierge - both Driven & Driving'
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: '30 Minutes FREE',
      description: 'No-obligation consultation to assess your needs'
    },
    {
      icon: Target,
      title: 'Personalized Roadmap',
      description: 'Custom strategy for your auto lifestyle goals'
    },
    {
      icon: Zap,
      title: 'Immediate Clarity',
      description: 'Walk away with actionable next steps'
    },
    {
      icon: Shield,
      title: 'Expert Guidance',
      description: 'Industry insights and insider knowledge'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative bg-black text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1614200179396-2bdb77ebf81b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXJ8ZW58MHx8fHwxNzYxNjEzMDE2fDA&ixlib=rb-4.1.0&q=85"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-block bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
            {content('hero.badge', 'FREE 30-MINUTE CONSULTATION')}
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            {content('hero.title', 'Stop Guessing. Get the Roadmap.').split('. ').map((part, i) => 
              i === 0 ? <span key={i}>{part}.<br /></span> : <span key={i}>Get the <span className="text-red-600">Roadmap</span>.</span>
            )}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            {content('hero.subtitle', "This isn't generic advice. We diagnose your situation, map the fastest path forward, and connect you with the right services—whether you need chauffeured transportation, a vehicle sourced, financing structured, or your car strategy rebuilt.")}
          </p>
          <div className="flex flex-wrap gap-4 justify-center text-sm text-gray-400">
            <span className="flex items-center"><CheckCircle className="h-4 w-4 mr-2 text-red-600" /> No Obligation</span>
            <span className="flex items-center"><CheckCircle className="h-4 w-4 mr-2 text-red-600" /> 100% Free</span>
            <span className="flex items-center"><CheckCircle className="h-4 w-4 mr-2 text-red-600" /> Expert Guidance</span>
          </div>
        </div>
      </div>

      {/* Benefits Grid */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-4 gap-6">
          {benefits.map((benefit, idx) => {
            const Icon = benefit.icon;
            return (
              <Card key={idx} className="p-6 text-center border-gray-200 hover:shadow-lg transition-all">
                <div className="h-14 w-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
                  <Icon className="h-7 w-7 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-black mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Consultation Form Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-4xl mx-auto px-6">
          {step < 4 ? (
            <>
              {/* Progress Indicator */}
              <div className="flex items-center justify-center mb-12">
                <div className="flex items-center">
                  {[1, 2, 3].map((s) => (
                    <React.Fragment key={s}>
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center font-bold ${
                        step >= s ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500'
                      }`}>
                        {step > s ? <CheckCircle className="h-5 w-5" /> : s}
                      </div>
                      {s < 3 && (
                        <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-red-600' : 'bg-gray-200'}`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <Card className="p-8 md:p-12 border-gray-200 shadow-xl">
                <form onSubmit={handleSubmit}>
                  {/* Step 1: Service Type */}
                  {step === 1 && (
                    <div className="space-y-8">
                      <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-black mb-2">What can we help you with?</h2>
                        <p className="text-gray-600">Select the type of service you're interested in</p>
                      </div>

                      <div className="grid md:grid-cols-3 gap-4">
                        {consultationTypes.map((type) => {
                          const Icon = type.icon;
                          return (
                            <button
                              key={type.value}
                              type="button"
                              onClick={() => {
                                handleSelectChange('consultationType', type.value);
                                setStep(2);
                              }}
                              className={`p-6 rounded-lg border-2 text-left transition-all hover:shadow-lg ${
                                formData.consultationType === type.value
                                  ? 'border-red-600 bg-red-50'
                                  : 'border-gray-200 hover:border-red-300'
                              }`}
                              data-testid={`consultation-type-${type.value}`}
                            >
                              <Icon className={`h-10 w-10 mb-4 ${
                                formData.consultationType === type.value ? 'text-red-600' : 'text-gray-400'
                              }`} />
                              <h3 className="text-lg font-bold text-black mb-2">{type.title}</h3>
                              <p className="text-sm text-gray-600">{type.description}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Step 2: Details */}
                  {step === 2 && (
                    <div className="space-y-6">
                      <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-black mb-2">Tell us more about your needs</h2>
                        <p className="text-gray-600">Help us prepare for your consultation</p>
                      </div>

                      {formData.consultationType === 'driven' && (
                        <>
                          <div>
                            <Label className="text-black mb-2 block">What type of transportation do you need?</Label>
                            <Select 
                              value={formData.vehicleInterest} 
                              onValueChange={(value) => handleSelectChange('vehicleInterest', value)}
                            >
                              <SelectTrigger data-testid="vehicle-interest-select">
                                <SelectValue placeholder="Select service type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="wedding">Wedding Transportation</SelectItem>
                                <SelectItem value="corporate">Corporate/Executive Transport</SelectItem>
                                <SelectItem value="airport">Airport Transfers</SelectItem>
                                <SelectItem value="special-event">Special Event</SelectItem>
                                <SelectItem value="ongoing">Ongoing Chauffeur Service</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}

                      {formData.consultationType === 'driving' && (
                        <>
                          <div>
                            <Label className="text-black mb-2 block">What are you looking for?</Label>
                            <Select 
                              value={formData.vehicleInterest} 
                              onValueChange={(value) => handleSelectChange('vehicleInterest', value)}
                            >
                              <SelectTrigger data-testid="driving-interest-select">
                                <SelectValue placeholder="Select your interest" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="buy-vehicle">Buy a Vehicle</SelectItem>
                                <SelectItem value="sell-trade">Sell/Trade My Vehicle</SelectItem>
                                <SelectItem value="auction-access">Auction Access</SelectItem>
                                <SelectItem value="fleet-management">Fleet Management</SelectItem>
                                <SelectItem value="financing">Financing Consultation</SelectItem>
                                <SelectItem value="maintenance">Maintenance Strategy</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}

                      {formData.consultationType === 'both' && (
                        <>
                          <div>
                            <Label className="text-black mb-2 block">Primary focus area</Label>
                            <Select 
                              value={formData.vehicleInterest} 
                              onValueChange={(value) => handleSelectChange('vehicleInterest', value)}
                            >
                              <SelectTrigger data-testid="both-interest-select">
                                <SelectValue placeholder="Select primary focus" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="full-lifestyle">Full Auto Lifestyle Management</SelectItem>
                                <SelectItem value="membership">Membership Inquiry</SelectItem>
                                <SelectItem value="corporate-account">Corporate Account Setup</SelectItem>
                                <SelectItem value="custom">Custom Requirements</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}

                      <div>
                        <Label className="text-black mb-2 block">Tell us about your current situation</Label>
                        <Textarea
                          name="currentSituation"
                          value={formData.currentSituation}
                          onChange={handleChange}
                          rows={4}
                          placeholder="What challenges are you facing? What are your goals?"
                          className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                          data-testid="current-situation-input"
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label className="text-black mb-2 block">Timeframe</Label>
                          <Select 
                            value={formData.timeframe} 
                            onValueChange={(value) => handleSelectChange('timeframe', value)}
                          >
                            <SelectTrigger data-testid="timeframe-select">
                              <SelectValue placeholder="When do you need this?" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="immediate">Immediately</SelectItem>
                              <SelectItem value="1-month">Within 1 Month</SelectItem>
                              <SelectItem value="3-months">Within 3 Months</SelectItem>
                              <SelectItem value="6-months">Within 6 Months</SelectItem>
                              <SelectItem value="exploring">Just Exploring</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-black mb-2 block">Budget Range (if applicable)</Label>
                          <Select 
                            value={formData.budget} 
                            onValueChange={(value) => handleSelectChange('budget', value)}
                          >
                            <SelectTrigger data-testid="budget-select">
                              <SelectValue placeholder="Select budget range" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="under-5k">Under $5,000</SelectItem>
                              <SelectItem value="5k-15k">$5,000 - $15,000</SelectItem>
                              <SelectItem value="15k-50k">$15,000 - $50,000</SelectItem>
                              <SelectItem value="50k-100k">$50,000 - $100,000</SelectItem>
                              <SelectItem value="100k-plus">$100,000+</SelectItem>
                              <SelectItem value="flexible">Flexible / TBD</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="flex gap-4 pt-6">
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => setStep(1)}
                          className="border-gray-300"
                        >
                          Back
                        </Button>
                        <Button 
                          type="button"
                          onClick={() => setStep(3)}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                          data-testid="step2-continue-btn"
                        >
                          Continue
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Contact Info */}
                  {step === 3 && (
                    <div className="space-y-6">
                      <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-black mb-2">Your Contact Information</h2>
                        <p className="text-gray-600">How should we reach you to schedule your consultation?</p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="firstName" className="text-black mb-2 block">First Name *</Label>
                          <Input
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            required
                            className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                            data-testid="first-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="lastName" className="text-black mb-2 block">Last Name *</Label>
                          <Input
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            required
                            className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                            data-testid="last-name-input"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="email" className="text-black mb-2 block">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                            data-testid="email-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="phone" className="text-black mb-2 block">Phone *</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                            data-testid="phone-input"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-black mb-3 block">Preferred Contact Method</Label>
                        <RadioGroup
                          value={formData.preferredContact}
                          onValueChange={(value) => handleSelectChange('preferredContact', value)}
                          className="flex gap-6"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="phone" id="phone-contact" />
                            <Label htmlFor="phone-contact" className="cursor-pointer">Phone Call</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="text" id="text-contact" />
                            <Label htmlFor="text-contact" className="cursor-pointer">Text Message</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="email" id="email-contact" />
                            <Label htmlFor="email-contact" className="cursor-pointer">Email</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      <div>
                        <Label className="text-black mb-2 block">Best Time to Reach You</Label>
                        <Select 
                          value={formData.preferredTime} 
                          onValueChange={(value) => handleSelectChange('preferredTime', value)}
                        >
                          <SelectTrigger data-testid="preferred-time-select">
                            <SelectValue placeholder="Select preferred time" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="morning">Morning (9am - 12pm)</SelectItem>
                            <SelectItem value="afternoon">Afternoon (12pm - 5pm)</SelectItem>
                            <SelectItem value="evening">Evening (5pm - 8pm)</SelectItem>
                            <SelectItem value="anytime">Anytime</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-black mb-2 block">Additional Notes (Optional)</Label>
                        <Textarea
                          name="additionalNotes"
                          value={formData.additionalNotes}
                          onChange={handleChange}
                          rows={3}
                          placeholder="Anything else we should know?"
                          className="border-gray-200 focus:border-red-600 focus:ring-red-600"
                          data-testid="additional-notes-input"
                        />
                      </div>

                      <div className="flex gap-4 pt-6">
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => setStep(2)}
                          className="border-gray-300"
                        >
                          Back
                        </Button>
                        <Button 
                          type="submit"
                          disabled={isSubmitting}
                          className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white text-lg"
                          data-testid="submit-consultation-btn"
                        >
                          {isSubmitting ? 'Submitting...' : 'Book My Free Consultation'}
                        </Button>
                      </div>
                    </div>
                  )}
                </form>
              </Card>
            </>
          ) : (
            /* Success State */
            <Card className="p-12 text-center border-gray-200 shadow-xl">
              <div className="h-20 w-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-black mb-4">Consultation Request Received!</h2>
              {submittedLead && (
                <p className="text-sm text-gray-500 mb-4">Reference: {submittedLead.leadId}</p>
              )}
              <p className="text-xl text-gray-600 mb-8">
                Thank you, {formData.firstName}! A member of our team will contact you within 24 hours to schedule your FREE 30-minute consultation.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/')}
                  size="lg"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Return Home
                </Button>
                <Button 
                  onClick={() => navigate('/membership')}
                  size="lg"
                  variant="outline"
                  className="border-gray-300"
                >
                  Explore Membership
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* What Happens Next */}
      {step < 4 && (
        <div className="max-w-7xl mx-auto px-6 py-20">
          <h2 className="text-3xl font-bold text-black text-center mb-12">What Happens After You Book?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="h-14 w-14 mx-auto rounded-full bg-red-600 text-white flex items-center justify-center text-2xl font-bold mb-4">
                1
              </div>
              <h3 className="text-xl font-bold text-black mb-2">We Review Your Info</h3>
              <p className="text-gray-600">Our team reviews your submission to understand your specific needs and prepare for our call.</p>
            </div>
            <div className="text-center">
              <div className="h-14 w-14 mx-auto rounded-full bg-red-600 text-white flex items-center justify-center text-2xl font-bold mb-4">
                2
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Schedule Your Call</h3>
              <p className="text-gray-600">We reach out within 24 hours to find a time that works for your schedule.</p>
            </div>
            <div className="text-center">
              <div className="h-14 w-14 mx-auto rounded-full bg-red-600 text-white flex items-center justify-center text-2xl font-bold mb-4">
                3
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Get Your Roadmap</h3>
              <p className="text-gray-600">Walk away with a clear strategy and actionable next steps for your auto lifestyle goals.</p>
            </div>
          </div>
        </div>
      )}

      {/* Urgency CTA */}
      {step < 4 && (
        <div className="bg-black text-white py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Calendar className="h-12 w-12 mx-auto text-red-600 mb-6" />
            <h2 className="text-3xl font-bold mb-4">Limited Consultation Slots Available</h2>
            <p className="text-lg text-gray-300 mb-6">
              Our consultations are in high demand. Book now to secure your spot and start your journey to smarter mobility.
            </p>
            <Button 
              onClick={() => window.scrollTo({ top: 400, behavior: 'smooth' })}
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Book My Free Consultation Now
            </Button>
          </div>
        </div>
      )}
      <CMSCustomSections pageId="consultation" dark />
    </div>
  );
};

export default Consultation;
